import type {
	HeaderNavButtonLabel,
	HeaderSubmenuButtonsLabel
} from '~/components/PortalHeader';
import type {
	HeaderClickAction,
	HeaderClicked,
	MenuHeaderClicked
} from '~/types/mixpanel';

import { MixpanelEvents, MixpanelProps } from '~/core/service/mixpanel';
import { safeTrack } from '~~/utils';

import {
	HeaderNavButtonEventsMapper,
	HeaderSubmenuEventsMapper
} from '~/components/PortalHeader';

export interface HeaderClickedEventProps {
	clickedButton: string;
	destinationUrl?: string;
	action: HeaderClickAction;
	clickRegion?: boolean;
	clickNeighborhood?: boolean;
	currentPage?: string;
	regionDescription?: string;
}

type handleEventPropsType = HeaderClickedEventProps | undefined;
export function handleEventProps(
	eventProps: handleEventPropsType,
	callback: (props: HeaderClickedEventProps) => void
) {
	if (eventProps) {
		callback(eventProps);
	}
}

const initializeMenuHeaderClickedProps = (
	props: HeaderClickedEventProps
): MenuHeaderClicked => {
	return {
		[MixpanelProps.CTA_TEXT]: props.clickedButton,
		[MixpanelProps.CLICK_OPTION]: props.action,
		[MixpanelProps.CLICK_EXCLUSIVE]: false,
		[MixpanelProps.CLICK_OFF_MARKET]: false,
		[MixpanelProps.CLICK_OWNER]: false,
		[MixpanelProps.CLICK_FORMER]: false,
		[MixpanelProps.CLICK_REGION]: props.clickRegion ?? false,
		[MixpanelProps.CLICK_NEIGHBORHOOD]: props.clickNeighborhood ?? false,
		[MixpanelProps.REGION_NAME]: props.clickRegion
			? props.clickedButton
			: undefined,
		[MixpanelProps.NEIGHBORHOOD_NAME]: props.clickNeighborhood
			? props.clickedButton
			: undefined,
		[MixpanelProps.CURRENT_PAGE]: props.currentPage ?? '',
		[MixpanelProps.DESTINATION_URL]: props.destinationUrl
	};
};

const initializeHeaderClickedProps = (
	clickedButton: HeaderNavButtonLabel,
	currentPage: string,
	destinationUrl?: string
): HeaderClicked => {
	const initialEventProps: HeaderClicked = {
		[MixpanelProps.CLICK_LOGO]: false,
		[MixpanelProps.CLICK_BUY]: false,
		[MixpanelProps.CLICK_SELL]: false,
		[MixpanelProps.CLICK_ABOUT_PILAR]: false,
		[MixpanelProps.CLICK_LOGIN]: false,
		[MixpanelProps.CLICK_BLOG]: false,
		[MixpanelProps.CTA_TEXT]: clickedButton,
		[MixpanelProps.CURRENT_PAGE]: currentPage
	};

	if (destinationUrl) {
		initialEventProps[MixpanelProps.DESTINATION_URL] = destinationUrl;
	}

	return initialEventProps;
};

export const useTrackHeader = () => {
	const route = useRoute();

	const trackEventHeader = (params: HeaderClicked) => {
		safeTrack(MixpanelEvents.HEADER_CLICKED, params);
	};

	const trackEventMenuHeader = (params: MenuHeaderClicked) => {
		safeTrack(MixpanelEvents.MENU_HEADER_CLICKED, params);
	};

	const handleEventHeaderClick = (
		clickedButton: HeaderNavButtonLabel,
		destinationUrl?: string
	) => {
		const initialEventProps = initializeHeaderClickedProps(
			clickedButton,
			route.fullPath,
			destinationUrl
		);
		const clickedProp = HeaderNavButtonEventsMapper[clickedButton];
		if (clickedProp) {
			(initialEventProps as any)[clickedProp] = true;
		}

		trackEventHeader(initialEventProps);
	};

	const handleEventMenuHeaderClick = (props: HeaderClickedEventProps) => {
		props.currentPage = route.fullPath;
		const initialEventProps = initializeMenuHeaderClickedProps(props);
		if (props.clickedButton in HeaderSubmenuEventsMapper) {
			const clickedProp =
				HeaderSubmenuEventsMapper[
					props.clickedButton as HeaderSubmenuButtonsLabel
				];
			(initialEventProps as any)[clickedProp] = true;
		}

		if (props.regionDescription) {
			initialEventProps[MixpanelProps.REGION_NAME] = props.regionDescription;
		}
		trackEventMenuHeader(initialEventProps);
	};

	return {
		handleEventHeaderClick,
		handleEventMenuHeaderClick
	};
};
